import { EntityType } from './entity-type.enum';
import { SystemCheckSums } from './index-db-keys';
import { UserType } from '../../core/global/constants/constant';

export enum ExecutionStatus {
  success,
  failure,
  warning,
}

export interface GenericError {
  message: string;
  caption: string;
  errorNumber: number;
}

export interface Warning {
  warningNumber: number;
  message: string;
  manualOverride: boolean;
  AvalilableOptions: Array<[string, string]>;
  OverrideOption: [string, string];
}

export interface Error {
  message: string;
  methodSignature: string;
  errorNumber: string;
}

export interface ExecutionResult {
  result: {
    status: ExecutionStatus;
    returnXml: string;
    errors: Error[];
    warnings: Warning[];
  };
  intermediateSessionId?: string;
  enrichedUsr?: SysUser;
}

export interface ExecuteQueryResult<T> extends ExecutionResult {
  records: T[],
}

export type Abe = Individual;


export const UserEntityTypeId = '6D6C84AF-A441-40C3-9C95-353083BE6A3F';

export class AvionBusinessEntity {
  AbeName?: string;
  EntityId?: string;
  EntityTypeId?: string;
  DisplayId?: number;
  PublicHospital?: boolean;
  CloneSourceId?: string;
  CreationDate?: Date;
  Creator?: string;
  CreatorDesc?: string;
  ModificationDate?: Date;
  Modifier?: string;
  ModifierDesc?: string;
  Description?: string;
  LegacySystemDescription?: any;
  LegacySystemIdentifier?: any;
  OwnerEntity?: string;
  OwnerEntityId?: string;
  OwnerEntityTypeId?: string;
  OwnerDescription?: any;
}

export class SysUser extends AvionBusinessEntity {
  __type?: string;
  UserName?: string;
  Password?: string;
  FirstName?: string;
  LastName?: string;
  WindowsAccount?: string;
  EmailAddress?: string;
  SessionId?: string = '';
  SiteId?: string;
  WebUserId?: string;
  IsSystem?: boolean;

  constructor() {
    super();
    this.EntityId = EntityType.emptyGUID;
    this.EntityTypeId = EntityType.sysUser;
  }
}

export class Request extends AvionBusinessEntity {
  Priority!: string;
  Details!: string;
  Title!: string;
  Type!: string;
  Status!: string;
  ClientStakeholder!: string;
  AvionStakeholder!: string;
  CurrentRequestOwner!: string;
  CCList!: string;
  PendingClientFeedback!: boolean;
  Environment!: string;
  EnvironmentDetails!: string;
  Boards!: { title: string, entityId: string }[];
  RequestClassification!: any;
  clientClosureReason!: string;
  csatRating?: number;
  clientClosureReasonNotes?: string;

  constructor() {
    super();
    this.EntityId = EntityType.emptyGUID;
    this.EntityTypeId = EntityType.request;
  }
}

export class Notification extends AvionBusinessEntity {
  Title!: string;
  Details!: string;
  NotificationSource!: string;
  Viewed!: boolean;
  ViewDate!: Date | null;
  __type?: string;

  constructor() {
    super();
    this.EntityId = EntityType.emptyGUID;
    this.EntityTypeId = EntityType.notification;
  }
}

export class ProjectInfo {
  projectId!: string;
  projectTitle!: string;
  projectRole!: string;
  NotifiyAllMembers!: boolean;
  OwnerEntityId!: string
  OwnerEntityTypeId!: string;
  PrimarySupportStaff!: string;
}

export class Project extends AvionBusinessEntity {
  public EnableMilestones!: boolean | null;
  public Title!: string;
  public RequestClassification!: string;
  public PrimarySupportStaff!: string;
  public NotifiyAllMembers!: boolean | null;
  public Details!: string;
  public __type: string;

  constructor() {
    super();
    this.EntityTypeId = EntityType.project;
    this.__type = "Project:#Avion.Genesis.Avalon.Core";
  }
}

export class ProjectClassifications extends AvionBusinessEntity {
  public RequestClassification!: string;
  public __type: string;

  constructor() {
    super();
    this.EntityId = EntityType.emptyGUID;
    this.EntityTypeId = EntityType.projectClassification;
    this.__type = "ProjectClassifications:#Avion.Genesis.Avalon.Core";
  }
}

export class UserInfo {
  userId!: string;
  firstName!: string;
  lastName!: string;
  displayName!: string;
  email!: string;
  username!: string;
  clientId!: string;
  clientName!: string;
  clientAcronym!: string;
  orgId!: string;
  accessibleProjects!: string;
  userType!: UserType;
  ownerId!: string;
  ownerTypeId!: string;
}

export class RequestInfo {
  projectEntityId!: string;
  projectId!: number;
  projectTitle!: string;
  requestEntityId!: string;
  requestId!: number;
  category!: string;
  requestTitle!: string;
  creationDate!: string;
  createdBy!: string;
  status!: string;
  lastCommentDate!: string;
  lastCommentBy!: string;
  files!: string;
  selected?: boolean;
  closureDateTime!: string;
}

export class TaskInfo {
  projectEntityId!: string;
  projectId!: number;
  projectTitle!: string;
  requestEntityId!: string;
  requestId!: number;
  requestTitle!: string;
  creationDate!: string;
  EntityId!: string;
  EntityTypeId!: string;
  OwnerEntityId!: string;
  OwnerEntityTypeId!: string;
  Title!: string;
  Status!: string;
  StatusText!: string;
  Category!: string;
  CategoryText!: string;
  TargetDate!: string;
  Details!: string;
  Milestone!: string;
  MilestoneText!: string;
}

export class Milestone extends AvionBusinessEntity {
  Title!: string;
  TargetDate!: string;
  PercentCompleted!: number;
  TaskCountByStatusJSON!: string;
  Details!: string;

  constructor() {
    super();
    this.EntityId = EntityType.emptyGUID;
    this.EntityTypeId = EntityType.milestone;
  }
}

export class MilestoneTask extends AvionBusinessEntity {
  Category!: string;
  CategoryText!: string;
  TargetDate!: string;
  StartDate!: string;
  Details!: string;
  Status!: string;
  StatusText!: string;
  Milestone!: string;
  MilestoneText!: string;
  Title!: string;
  // Set server-side from the author of the most recent comment: true = the task is sitting with
  // the client, false = it is with Avion. Null until the first comment is saved, and the ABE
  // payload delivers the bit as a boolean, 0/1, or "true"/"false" depending on the serialiser —
  // the union is deliberate so a consumer cannot assume a plain boolean and write `=== false`,
  // which silently misreads null. Always read it through readPendingClientFeedback in
  // task-ownership.util, which resolves every form to one boolean.
  PendingClientFeedback?: boolean | number | string | null;

  constructor() {
    super();
    this.EntityId = EntityType.emptyGUID;
    this.EntityTypeId = EntityType.task;
  }
}

export class Comment extends AvionBusinessEntity {
  Comment!: string;
  ReassignedTo!: string;
  ReassignedToDetails?: string;
  TimeSpent!: number;
  IsInternalComment!: boolean;
  AvionComment!: boolean;
  ShiftRequestOwnership!: boolean;

  constructor() {
    super();
    this.EntityId = EntityType.emptyGUID;
    this.EntityTypeId = EntityType.comment;
  }
}

export class SysGroup extends AvionBusinessEntity {
  Groupname!: string;

  constructor() {
    super();
    this.EntityId = EntityType.emptyGUID;
    this.EntityTypeId = EntityType.sysGroup;
  }
}

export interface RelatedEntities {
  AbeTypeId: string;
  AbeType: string;
  NumRelations: number;
}

export interface GlobalConfig {
  ConfigKey: string;
  ConfigValue: string;
}

export class InsightMap extends AvionBusinessEntity {
  title?: string;
  mapJson?: string;
  details?: string;
  __type?: string;

  constructor() {
    super();
    this.EntityTypeId = EntityType.insightMap;
    this.EntityId = EntityType.emptyGUID;
  }
}

export const individualCurrentStatus = 'Provisional';

export class Individual extends AvionBusinessEntity {
  ReciprocalId?: number;
  SubType?: string;
  Extra2: string = '';
  MiddleName: string = '';
  Aboriginal?: boolean;
  PostGradQualsOther: string = '';
  StatementOfAccountEmailCC: string = '';
  RACSID: string = '';
  DefaultPwd: string = '';
  OrganisationName: string = '';
  OrganisationType: string = '';
  MBBSGraduationDate?: Date;
  Title?: string;
  Webuserid?: string;
  TitleName: string = '';
  EmailAddress: string = '';
  Citizenship?: string;
  associations: string = '';
  GraduateInstitute: string = '';
  Gender?: string;
  GenderName?: string;
  Spouse: string = '';
  TorresStraitIslander?: boolean;
  TrainingCompletionYear?: number;
  Qualifications: string = '';
  OTPMode: string = '';
  Status?: string;
  Metro?: boolean;
  JobTitle: string = '';
  SocietyMemberships: string = '';
  PracticingStatus?: string;
  awards: string = '';
  DateDeceased?: Date;
  SourceCode: string = '';
  Branch?: string;
  Extra1: string = '';
  Languages: string = '';
  Hospitals: string = '';
  StatementOfAccountEmail: string = '';
  WebUserId: string = '';
  PostNoms: string = '';
  ExtraJSON?: null | string;
  TFN!: string;
  ABN!: string;
  OperatingStatus?: string;
  FirstName: string = '';
  RelatedEntities?: string;
  Extra4: string = '';
  BirthDate?: Date;
  OTPTarget: string = '';
  LatestMembershipInvoice?: string;
  associateyear?: number;
  LastName: string = '';
  PostGradQuals: string = '';
  SubspecialityAreas: string = '';
  Type?: string;
  FellowYear?: number;
  CurrentStatus!: string;
  Extra3: string = '';
  overseas?: boolean;
  StateOfTraining: string = '';
  RegistrationNumber: string = '';
  PreferredName: string = '';
  __type: string;

  constructor() {
    super();
    this.EntityTypeId = EntityType.individual;
    this.EntityId = EntityType.emptyGUID;
    this.__type = 'individual:#Avion.Genesis.Avalon.Core';
    this.CurrentStatus = individualCurrentStatus;
  }
}

export class Board extends AvionBusinessEntity {
  StartDate!: Date;
  EndDate!: Date;
  Details!: string;
  Title!: string;
  AutoAddNew!: boolean;
  AutoRemoveClosed!: boolean;
  __type: string;


  constructor() {
    super();
    this.EntityTypeId = EntityType.board;
    this.EntityId = EntityType.emptyGUID;
    this.__type = 'Board:#Avion.Genesis.Avalon.Core';
  }
}

export class Phone extends AvionBusinessEntity {
  IsPrimary?: boolean;
  Extension: string = '';
  PhoneNumber: string = '';
  type?: string;
  Type?: string;
  TypeName?: string;
  __type?: string;

  constructor() {
    super();
    this.EntityTypeId = EntityType.phone;
    this.EntityId = '00000000-0000-0000-0000-000000000000';
    this.__type = 'phone:#Avion.Genesis.Avalon.Core';
  }
}

export class BoardList extends AvionBusinessEntity {
  Details!: string;
  Title!: string;
  Position!: number;
  __type: string;
  requests?: BoardListRequest[];

  constructor() {
    super();
    this.EntityTypeId = EntityType.boardList;
    this.EntityId = EntityType.emptyGUID;
    this.__type = 'BoardList:#Avion.Genesis.Avalon.Core';
  }
}

export class BoardListRequest extends AvionBusinessEntity {
  Position!: number;
  requestEntity!: string;
  requestTitle!: string;
  requestDetails!: string;
  __type: string;
  requestId!: string;

  constructor() {
    super();
    this.EntityTypeId = EntityType.boardListRequest;
    this.EntityId = EntityType.emptyGUID;
    this.__type = 'BoardListRequest:#Avion.Genesis.Avalon.Core';
  }
}

export class ClientEnvironment extends AvionBusinessEntity {
  Title!: string;
  Details!: string;
  AvionHosted!: boolean;
  __type: string;

  constructor() {
    super();
    this.EntityTypeId = EntityType.clientEnvironment;
    this.EntityId = EntityType.emptyGUID;
    this.__type = 'BoardListRequest:#Avion.Genesis.Avalon.Core';
  }
}

export interface ListResult<T> extends ExecutionResult {
  abeList: T[];
}

export interface RequestSummary {
  Category: string,
  Status: string,
  Counts: string,
}

export interface OpenTicketsSummary {
  ReviewStatus: string,
  Counts: string,
}

export interface RequestTrends {
  AnalysisDate: string;
  ClientID: string;
  ClientName: string;
  NumberOfNewRequestsCreated: string;
  NumberOfOpenRequests: string
  NumberOfOpenRequestsPendingInvestigation: string;
  NumberOfOpenRequestsPendingUserFeedback: string;
  NumberOfRequestsClosed: string;
  ProjectID: string;
  ProjectTitle: string;
}

export interface SystemCheckSumsResult {
  result: {
    status: ExecutionStatus;
    returnXml: string;
    errors: GenericError[];
    warnings: Warning[];
  };
  systemCheckSums: SystemCheckSums[];
}

export interface AskAvI {
  UserAge: string;
  UserCountry: string;
  UserPrompt: string;
  Question: string;
}
